/**
 * Roomgrid design system — tokens + shared theme builder.
 *
 * Port of the Roomgrid system used by Locks Management Admin
 * (src/styles-roomgrid.scss there) to an MUI v5 theme, following the
 * Claude Design handoff "Notify Admin × Roomgrid".
 *
 * One token dictionary feeds both themes; `buildRoomgridTheme('light'|'dark')`
 * produces the full theme. lightTheme.ts / darkTheme.ts are thin wrappers.
 *
 * Ground rules encoded here:
 *  - Warm neutrals, not cool grays.
 *  - Primary buttons are near-black (--text). Teal accent is reserved for
 *    focus, selection, active nav, chips and info.
 *  - JetBrains Mono (tabular-nums) for data: IDs, dates, counters, KPIs,
 *    pagination. Exposed as the `.rg-mono` utility class + `custom.rg.fontMono`.
 *  - Cards are flat (border, radius 14, no shadow). Shadows only on
 *    modals (elevation 24), popovers/menus (8) and toasts (6).
 */

import { defaultTheme } from "react-admin";
import { createTheme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";

// ============================================
// TOKENS
// ============================================

export interface RgTriplet {
  bg: string;
  fg: string;
  line: string;
}

export interface RgTokens {
  mode: "light" | "dark";
  bg: string;
  bg2: string;
  bgElev: string;
  border: string;
  borderStrong: string;
  text: string;
  text2: string;
  text3: string;
  text4: string;
  accent: string;
  accentWeak: string;
  ok: RgTriplet;
  danger: RgTriplet;
  warn: RgTriplet;
  info: RgTriplet;
  neutral: RgTriplet;
  violet: RgTriplet;
  shadowModal: string;
  shadowPopover: string;
  shadowToast: string;
  focusRing: string;
  overlay: string;
  fontUi: string;
  fontMono: string;
}

const fontUi = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
const fontMono = '"JetBrains Mono", "SF Mono", ui-monospace, monospace';

// Hex values are exact sRGB conversions of the handoff's OKLCH tokens, so
// MUI color utilities (alpha, darken…) can parse them.
export const rgLight: RgTokens = {
  mode: "light",
  bg: "#fafaf7",
  bg2: "#f3f2ec",
  bgElev: "#ffffff",
  border: "#e5e3db",
  borderStrong: "#d8d5ca",
  text: "#1a1917",
  text2: "#4a4840",
  text3: "#8c8a80",
  text4: "#b8b5a8",
  accent: "#008aa3", // oklch(0.58 0.11 215)
  accentWeak: "#d2edf4", // oklch(0.93 0.03 215)
  ok: { bg: "#a3eac3", fg: "#004120", line: "#007343" },
  danger: { bg: "#ffd7cd", fg: "#892218", line: "#c35141" },
  warn: { bg: "#fcf1d4", fg: "#6f5000", line: "#ba9300" },
  info: { bg: "#d2edf4", fg: "#008aa3", line: "#008aa3" },
  neutral: { bg: "#e4ecf9", fg: "#4c5666", line: "#8590a2" },
  violet: { bg: "#e9deff", fg: "#583a84", line: "#8c6ebd" },
  shadowModal: "0 24px 64px rgba(0,0,0,.28)",
  shadowPopover: "0 20px 50px rgba(0,0,0,.22)",
  shadowToast: "0 12px 32px rgba(0,0,0,.24)",
  focusRing: "0 0 0 3px #d2edf4",
  overlay: "rgba(0,0,0,.42)",
  fontUi,
  fontMono,
};

export const rgDark: RgTokens = {
  mode: "dark",
  bg: "#141412",
  bg2: "#1c1b18",
  bgElev: "#222220",
  border: "#2a2926",
  borderStrong: "#3a3934",
  text: "#f2efe6",
  text2: "#c4c0b3",
  text3: "#8a877c",
  text4: "#575549",
  accent: "#49bfd9", // oklch(0.75 0.11 215)
  accentWeak: "#05343d", // oklch(0.30 0.05 215)
  ok: { bg: "#0a3723", fg: "#a3eac3", line: "#118659" },
  danger: { bg: "#4b1e18", fg: "#ffd0c7", line: "#c65b4c" },
  warn: { bg: "#392c01", fg: "#f9e3a7", line: "#b18e15" },
  info: { bg: "#05343d", fg: "#49bfd9", line: "#49bfd9" },
  neutral: { bg: "#282e38", fg: "#d6dfec", line: "#677284" },
  violet: { bg: "#332647", fg: "#e3d7fb", line: "#9076be" },
  shadowModal: "0 24px 64px rgba(0,0,0,.55)",
  shadowPopover: "0 20px 50px rgba(0,0,0,.45)",
  shadowToast: "0 12px 32px rgba(0,0,0,.5)",
  focusRing: "0 0 0 3px #05343d",
  overlay: "rgba(0,0,0,.55)",
  fontUi,
  fontMono,
};

// ============================================
// TYPE DECLARATIONS
// ============================================

declare module "@mui/material/styles" {
  interface Theme {
    custom: {
      rg: RgTokens;
      radius: { control: string; card: string; modal: string; pill: string };
      layout: {
        sidebarWidth: string;
        sidebarRailWidth: string;
        topbarHeight: string;
        contentPadding: string;
      };
    };
  }

  interface ThemeOptions {
    custom?: Theme["custom"];
  }

  interface Palette {
    white: string;
  }

  interface PaletteOptions {
    white?: string;
  }

  interface Components {
    RaList?: { styleOverrides?: { root?: any } };
    RaDatagrid?: { styleOverrides?: { root?: any } };
  }
}

// ============================================
// THEME BUILDER
// ============================================

const RADIUS = { control: "8px", card: "14px", modal: "16px", pill: "99px" };
const LAYOUT = {
  sidebarWidth: "222px",
  sidebarRailWidth: "64px",
  topbarHeight: "56px",
  contentPadding: "20px 24px",
};

// Micro-label: table headers, form labels, sidebar group headers.
export const rgMicro = (t: RgTokens) => ({
  fontFamily: t.fontUi,
  fontSize: "10.5px",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  fontWeight: 600,
  color: t.text3,
});

export const rgMono = (t: RgTokens) => ({
  fontFamily: t.fontMono,
  fontVariantNumeric: "tabular-nums",
  letterSpacing: "-0.01em",
});

export const buildRoomgridTheme = (mode: "light" | "dark") => {
  const t = mode === "light" ? rgLight : rgDark;

  // Shadows only on floating surfaces: Snackbar content (6),
  // Menu/Popover (8), Dialog (24). Everything else is flat.
  const shadows = Array(25).fill("none") as Shadows;
  shadows[6] = t.shadowToast;
  shadows[8] = t.shadowPopover;
  shadows[24] = t.shadowModal;

  const warmGrey = {
    50: t.bg,
    100: t.bg2,
    200: t.border,
    300: t.borderStrong,
    400: t.text4,
    500: t.text3,
    600: t.text2,
    700: mode === "light" ? "#3a3934" : "#d8d5ca",
    800: mode === "light" ? "#2a2926" : "#e5e3db",
    900: t.text,
  };

  const base = createTheme({
    ...defaultTheme,
    // react-admin's <Sidebar> reads these to size the drawer paper — keep
    // them in lockstep with custom.layout.sidebarWidth / sidebarRailWidth.
    ...({ sidebar: { width: 222, closedWidth: 64 } } as object),
    palette: {
      mode,
      // Primary = near-black. NEVER teal — teal lives in `secondary`/`info`
      // and is reserved for focus, selection, active nav and chips.
      primary: {
        main: t.text,
        light: t.text2,
        dark: t.text,
        contrastText: t.bgElev,
      },
      secondary: {
        main: t.accent,
        light: t.accentWeak,
        dark: t.accent,
        contrastText: mode === "light" ? "#ffffff" : t.bg,
      },
      background: { default: t.bg, paper: t.bgElev },
      text: { primary: t.text, secondary: t.text2, disabled: t.text4 },
      success: {
        light: t.ok.bg,
        main: t.ok.line,
        dark: t.ok.fg,
        contrastText: "#ffffff",
      },
      error: {
        light: t.danger.bg,
        main: t.danger.line,
        dark: t.danger.fg,
        contrastText: "#ffffff",
      },
      warning: {
        light: t.warn.bg,
        main: t.warn.line,
        dark: t.warn.fg,
        contrastText: "#ffffff",
      },
      info: {
        light: t.info.bg,
        main: t.info.line,
        dark: t.info.fg,
        contrastText: mode === "light" ? "#ffffff" : t.bg,
      },
      divider: t.border,
      grey: warmGrey,
      action: {
        hover: t.bg2,
        selected: t.accentWeak,
        disabled: t.text4,
        disabledBackground: t.bg2,
        focus: t.accentWeak,
      },
      white: "#ffffff",
    },
    shape: { borderRadius: 8 },
    shadows,
    custom: {
      rg: t,
      radius: RADIUS,
      layout: LAYOUT,
    },
    typography: {
      fontFamily: t.fontUi,
      fontSize: 13,
      htmlFontSize: 16,
      h1: { fontSize: "26px", fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontSize: "22px", fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontSize: "19px", fontWeight: 700, letterSpacing: "-0.02em" },
      h4: { fontSize: "17px", fontWeight: 700, letterSpacing: "-0.02em" },
      // Topbar page title per handoff.
      h5: { fontSize: "19px", fontWeight: 700, letterSpacing: "-0.02em" },
      h6: { fontSize: "15px", fontWeight: 700, letterSpacing: "-0.02em" },
      subtitle1: { fontSize: "13px", fontWeight: 600 },
      subtitle2: { fontSize: "12.5px", fontWeight: 600 },
      body1: { fontSize: "13px", lineHeight: 1.45 },
      body2: { fontSize: "12.5px", lineHeight: 1.45 },
      caption: { fontSize: "11.5px", lineHeight: 1.4 },
      overline: { ...rgMicro(t), lineHeight: 1.6 },
      button: { fontSize: "13px", fontWeight: 500, textTransform: "none" },
    },
  });

  return createTheme(base, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Publish tokens as CSS variables so plain CSS/SVG (charts,
          // TipTap content) can consume them.
          ":root": {
            "--rg-bg": t.bg,
            "--rg-bg-2": t.bg2,
            "--rg-bg-elev": t.bgElev,
            "--rg-border": t.border,
            "--rg-border-strong": t.borderStrong,
            "--rg-text": t.text,
            "--rg-text-2": t.text2,
            "--rg-text-3": t.text3,
            "--rg-text-4": t.text4,
            "--rg-accent": t.accent,
            "--rg-accent-weak": t.accentWeak,
            "--rg-ok-bg": t.ok.bg,
            "--rg-ok-fg": t.ok.fg,
            "--rg-ok-line": t.ok.line,
            "--rg-danger-bg": t.danger.bg,
            "--rg-danger-fg": t.danger.fg,
            "--rg-danger-line": t.danger.line,
            "--rg-warn-bg": t.warn.bg,
            "--rg-warn-fg": t.warn.fg,
            "--rg-warn-line": t.warn.line,
            "--rg-neutral-bg": t.neutral.bg,
            "--rg-neutral-fg": t.neutral.fg,
            "--rg-neutral-line": t.neutral.line,
            "--rg-violet-bg": t.violet.bg,
            "--rg-violet-fg": t.violet.fg,
            "--rg-violet-line": t.violet.line,
          },
          body: {
            backgroundColor: t.bg,
            color: t.text,
            fontFamily: t.fontUi,
            fontSize: "13px",
            lineHeight: 1.45,
            WebkitFontSmoothing: "antialiased",
            fontFeatureSettings: '"cv11", "ss01"',
            transition: "background-color 0.25s ease, color 0.25s ease",
          },
          // Utility classes (mirror .mono / .micro from locks Roomgrid).
          ".rg-mono": rgMono(t),
          ".rg-micro": rgMicro(t),
          "::selection": {
            backgroundColor: t.accentWeak,
            color: t.mode === "light" ? t.text : t.text,
          },
          // Thin scrollbars, pill thumb.
          "*::-webkit-scrollbar": { width: 8, height: 8 },
          "*::-webkit-scrollbar-track": { background: "transparent" },
          "*::-webkit-scrollbar-thumb": {
            background: t.borderStrong,
            borderRadius: 99,
          },
          "*::-webkit-scrollbar-thumb:hover": { background: t.text4 },
          "*": { scrollbarColor: `${t.borderStrong} transparent` },
          // Global focus ring.
          ":focus-visible": {
            outline: "none",
            boxShadow: t.focusRing,
          },
          // Native file inputs (Insert image / Attach file dialogs).
          'input[type="file"]': { color: t.text, fontSize: "12.5px" },
          'input[type="file"]::file-selector-button': {
            marginRight: 12,
            padding: "6px 12px",
            borderRadius: RADIUS.control,
            border: `1px solid ${t.border}`,
            background: t.bg2,
            color: t.text,
            fontSize: "12.5px",
            fontWeight: 500,
            fontFamily: t.fontUi,
            cursor: "pointer",
          },
          'input[type="file"]::file-selector-button:hover': {
            borderColor: t.borderStrong,
          },
        },
      },

      // ---- Buttons ----
      MuiButton: {
        defaultProps: { size: "small" as const, disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 36,
            borderRadius: RADIUS.control,
            padding: "0 14px",
            fontSize: "13px",
            fontWeight: 500,
            gap: 7,
            textTransform: "none" as const,
            boxShadow: "none",
            "&.Mui-disabled": { opacity: 0.45, color: undefined },
            "&:focus-visible": { boxShadow: t.focusRing },
          },
          sizeSmall: { minHeight: 30, padding: "0 12px", borderRadius: "7px" },
          sizeLarge: { minHeight: 40, padding: "0 16px" },
          // Primary CTA: near-black fill, settle via opacity — never teal.
          containedPrimary: {
            backgroundColor: t.text,
            color: t.bgElev,
            "&:hover": { backgroundColor: t.text, opacity: 0.88 },
            "&.Mui-disabled": {
              backgroundColor: t.text,
              color: t.bgElev,
            },
          },
          containedError: {
            backgroundColor: t.danger.line,
            color: "#ffffff",
            fontWeight: 600,
            "&:hover": { backgroundColor: t.danger.line, opacity: 0.88 },
          },
          containedSuccess: {
            backgroundColor: t.ok.line,
            color: "#ffffff",
            fontWeight: 600,
            "&:hover": { backgroundColor: t.ok.line, opacity: 0.88 },
          },
          containedSecondary: {
            backgroundColor: t.accent,
            "&:hover": { backgroundColor: t.accent, opacity: 0.88 },
          },
          // Secondary: sunken fill + hairline border.
          outlined: {
            backgroundColor: t.bg2,
            borderColor: t.border,
            color: t.text,
            "&:hover": {
              backgroundColor: t.bg2,
              borderColor: t.borderStrong,
            },
          },
          outlinedError: {
            backgroundColor: "transparent",
            borderColor: t.danger.line,
            color: t.danger.line,
            "&:hover": {
              backgroundColor: t.danger.bg,
              borderColor: t.danger.line,
            },
          },
          // Ghost.
          text: {
            color: t.text2,
            "&:hover": { backgroundColor: t.bg2 },
          },
          textError: {
            color: t.danger.line,
            "&:hover": { backgroundColor: t.danger.bg },
          },
        },
      },

      // Row-action style icon buttons: 28px square, hairline border.
      MuiIconButton: {
        styleOverrides: {
          root: {
            width: 28,
            height: 28,
            borderRadius: "7px",
            border: `1px solid ${t.border}`,
            color: t.text2,
            padding: 0,
            "&:hover": { backgroundColor: t.bg2 },
            "&:focus-visible": { boxShadow: t.focusRing },
            "& .MuiSvgIcon-root": { fontSize: 16 },
          },
          colorError: {
            color: t.danger.line,
            borderColor: t.danger.line,
            "&:hover": { backgroundColor: t.danger.bg },
          },
          colorSecondary: {
            color: t.accent,
            borderColor: t.accent,
            "&:hover": { backgroundColor: t.accentWeak },
          },
        },
      },

      // ---- Surfaces ----
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: {
            borderColor: t.border,
            borderRadius: RADIUS.card,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: t.bgElev,
            border: `1px solid ${t.border}`,
            borderRadius: RADIUS.card,
            boxShadow: "none",
          },
        },
      },

      // ---- Dialogs / overlays ----
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: t.bgElev,
            border: `1px solid ${t.border}`,
            borderRadius: RADIUS.modal,
            boxShadow: t.shadowModal,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: "15px",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            padding: "16px 20px",
            borderBottom: `1px solid ${t.border}`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: "18px 20px" },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "14px 20px",
            backgroundColor: t.bg2,
            borderTop: `1px solid ${t.border}`,
            gap: 8,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: t.overlay,
            backdropFilter: "blur(2px)",
            "&.MuiBackdrop-invisible": {
              backgroundColor: "transparent",
              backdropFilter: "none",
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: t.bgElev,
            border: `1px solid ${t.border}`,
            borderRadius: "12px",
            boxShadow: t.shadowPopover,
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: "12.5px",
            borderRadius: "7px",
            minHeight: 30,
            "&:hover": { backgroundColor: t.bg2 },
            "&.Mui-selected": {
              backgroundColor: t.accentWeak,
              "&:hover": { backgroundColor: t.accentWeak },
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: t.bgElev,
            border: `1px solid ${t.border}`,
            borderRadius: "12px",
            boxShadow: t.shadowPopover,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.text,
            color: t.bgElev,
            fontSize: "11.5px",
            fontWeight: 500,
            borderRadius: "8px",
            padding: "5px 10px",
          },
          arrow: { color: t.text },
        },
      },

      // ---- Data display ----
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.pill,
            height: 22,
            fontSize: "11px",
            fontWeight: 600,
            backgroundColor: t.neutral.bg,
            color: t.neutral.fg,
          },
          label: { paddingLeft: 10, paddingRight: 10 },
          sizeSmall: { height: 20, fontSize: "10.5px" },
          colorPrimary: { backgroundColor: t.accentWeak, color: t.accent },
          colorSecondary: { backgroundColor: t.accentWeak, color: t.accent },
          colorSuccess: { backgroundColor: t.ok.bg, color: t.ok.fg },
          colorError: { backgroundColor: t.danger.bg, color: t.danger.fg },
          colorWarning: { backgroundColor: t.warn.bg, color: t.warn.fg },
          colorInfo: { backgroundColor: t.info.bg, color: t.info.fg },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: t.accentWeak,
            color: t.accent,
            fontWeight: 700,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: "10px", fontSize: "12.5px" },
          standardSuccess: { backgroundColor: t.ok.bg, color: t.ok.fg },
          standardError: { backgroundColor: t.danger.bg, color: t.danger.fg },
          standardWarning: { backgroundColor: t.warn.bg, color: t.warn.fg },
          standardInfo: { backgroundColor: t.info.bg, color: t.info.fg },
        },
      },

      // ---- Tables (MUI + react-admin datagrid) ----
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              ...rgMicro(t),
              backgroundColor: t.bg2,
              borderBottom: `1px solid ${t.border}`,
              padding: "9px 14px",
              whiteSpace: "nowrap" as const,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: "13px",
            padding: "10px 14px",
            borderBottom: `1px solid ${t.border}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: t.bg2 },
            "&.MuiTableRow-head:hover": { backgroundColor: "transparent" },
          },
        },
      },
      RaDatagrid: {
        styleOverrides: {
          root: {
            backgroundColor: t.bgElev,
            border: `1px solid ${t.border}`,
            borderRadius: RADIUS.card,
            overflow: "hidden",
            "& .RaDatagrid-headerCell": {
              ...rgMicro(t),
              backgroundColor: t.bg2,
              borderBottom: `1px solid ${t.border}`,
              padding: "9px 14px",
              whiteSpace: "nowrap" as const,
              "& .MuiTableSortLabel-root": {
                color: "inherit",
                "&.Mui-active": { color: t.accent },
                "&:hover": { color: t.text2 },
              },
              "& .MuiTableSortLabel-icon": { color: `${t.accent} !important` },
            },
            "& .RaDatagrid-row": {
              backgroundColor: "transparent",
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: t.bg2 },
            },
            "& .RaDatagrid-rowCell": {
              fontSize: "13px",
              padding: "10px 14px",
              borderBottom: `1px solid ${t.border}`,
            },
            "tbody tr:last-child .RaDatagrid-rowCell": {
              borderBottom: "none",
            },
          },
        },
      },
      RaList: {
        styleOverrides: {
          root: {
            "& .RaList-main": {
              paddingTop: 0,
              paddingLeft: 0,
              paddingRight: 0,
            },
          },
        },
      },

      // ---- Inputs ----
      MuiTextField: {
        defaultProps: { variant: "outlined" as const, size: "small" as const },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.bg2,
            borderRadius: "9px",
            fontSize: "13px",
            transition:
              "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
            // Labels live OUTSIDE the field (see MuiInputLabel) — kill the
            // notch gap the floating label would have carved in the border.
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: t.border,
              top: 0,
              "& legend": { display: "none" },
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: t.borderStrong,
            },
            "&.Mui-focused": {
              backgroundColor: t.bgElev,
              boxShadow: t.focusRing,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: t.accent,
                borderWidth: 1,
              },
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: t.danger.line,
            },
          },
          input: {
            "&::placeholder": { color: t.text4, opacity: 1 },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "&.MuiInputBase-sizeSmall": { minHeight: 34 },
            "&:not(.MuiInputBase-sizeSmall)": { minHeight: 38 },
          },
        },
      },
      // Micro label ABOVE the field (Roomgrid) — never floating inside it.
      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: {
            position: "static" as const,
            transform: "none",
            pointerEvents: "auto",
            marginBottom: "5px",
            maxWidth: "100%",
            ...rgMicro(t),
            "&.Mui-focused": { color: t.accent },
            "&.Mui-error": { color: t.danger.line },
            "&.Mui-disabled": { color: t.text4 },
            "& .MuiFormLabel-asterisk": { color: t.danger.line },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { fontSize: "11px", marginLeft: 2 },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: t.text4,
            "&.Mui-checked": { color: t.accent },
            "& .MuiSvgIcon-root": { fontSize: 18 },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: t.text4,
            "&.Mui-checked": { color: t.accent },
          },
        },
      },
      // Compact 36×21 pill. Every rule doubled ("&&") so it beats the
      // library defaults regardless of style-injection order.
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&&": {
              width: 36,
              height: 21,
              padding: 0,
              overflow: "visible",
            },
            "&& .MuiSwitch-switchBase": {
              padding: "2.5px",
              border: "none",
              "&.Mui-checked": {
                transform: "translateX(15px)",
                color: "#ffffff",
              },
              "&.Mui-checked + .MuiSwitch-track": {
                backgroundColor: t.accent,
                opacity: 1,
                border: "none",
              },
            },
            "&& .MuiSwitch-thumb": {
              width: 16,
              height: 16,
              backgroundColor: "#ffffff",
              boxShadow: "0 1px 2px rgba(0,0,0,.2)",
            },
            "&& .MuiSwitch-track": {
              borderRadius: 99,
              backgroundColor: t.borderStrong,
              opacity: 1,
            },
          },
        },
      },

      // ---- Segmented control ----
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            border: `1px solid ${t.border}`,
            borderRadius: RADIUS.control,
            overflow: "hidden",
            backgroundColor: "transparent",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: "none",
            borderRadius: 0,
            minHeight: 28,
            padding: "2px 12px",
            fontSize: "11.5px",
            fontWeight: 600,
            textTransform: "none" as const,
            color: t.text2,
            "&:not(:first-of-type)": {
              borderLeft: `1px solid ${t.border}`,
            },
            "&.Mui-selected": {
              backgroundColor: t.text,
              color: t.bgElev,
              "&:hover": { backgroundColor: t.text, opacity: 0.88 },
            },
            "&:hover": { backgroundColor: t.bg2 },
          },
        },
      },

      // ---- Tabs ----
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { backgroundColor: t.accent, height: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none" as const,
            fontSize: "13px",
            fontWeight: 600,
            minHeight: 40,
            color: t.text2,
            "&.Mui-selected": { color: t.text },
          },
        },
      },

      // ---- Pagination (mono, near-black current page) ----
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            ...rgMono(t),
            minWidth: 30,
            height: 30,
            margin: "0 3px",
            borderRadius: RADIUS.control,
            fontSize: "12px",
            fontWeight: 500,
            color: t.text2,
            border: `1px solid ${t.border}`,
            "&:hover": { backgroundColor: t.bg2 },
            "&.Mui-selected": {
              backgroundColor: t.text,
              color: t.bgElev,
              border: "none",
              "&:hover": { backgroundColor: t.text, opacity: 0.88 },
            },
            "&.Mui-disabled": { opacity: 0.45 },
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: { display: "flex", justifyContent: "flex-end" },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            backgroundColor: "transparent",
            borderTop: `1px solid ${t.border}`,
            "& .MuiTablePagination-toolbar": {
              minHeight: 50,
              paddingLeft: 14,
              paddingRight: 14,
              gap: 8,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                ...rgMono(t),
                fontSize: "11.5px",
                color: t.text3,
                marginBottom: 0,
              },
            "& .MuiTablePagination-select": {
              ...rgMono(t),
              fontSize: "11.5px",
              color: t.text,
              border: `1px solid ${t.border}`,
              borderRadius: RADIUS.control,
              backgroundColor: t.bgElev,
              paddingTop: 4,
              paddingBottom: 4,
              paddingLeft: 8,
              "&:focus": {
                backgroundColor: t.bgElev,
                borderColor: t.accent,
                borderRadius: RADIUS.control,
              },
            },
            "& .MuiTablePagination-actions .MuiIconButton-root": {
              width: 30,
              height: 30,
              borderRadius: RADIUS.control,
              margin: "0 2px",
              color: t.text2,
              "&:hover": { backgroundColor: t.bg2 },
              "&.Mui-disabled": { opacity: 0.45, color: t.text4 },
            },
          },
        },
      },

      // ---- Toasts: inverted, bottom-center (anchor set in RgNotification) ----
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: t.text,
            color: t.bgElev,
            borderRadius: "10px",
            boxShadow: t.shadowToast,
            fontSize: "12.5px",
            fontWeight: 500,
            padding: "4px 16px",
            minWidth: "auto",
          },
        },
      },

      // ---- Misc ----
      MuiCircularProgress: {
        styleOverrides: {
          root: { color: t.accent },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { backgroundColor: t.bg2 },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: t.border },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: t.accent },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: t.bgElev,
            color: t.text,
            backgroundImage: "none",
            boxShadow: "none",
            borderBottom: `1px solid ${t.border}`,
          },
        },
      },
    },
  });
};
